/**
 * TalentGridExport — the grid as a PDF and as a spreadsheet (a campus GM, 8 Sep
 * 2026: "export the image with the actual names"; HR: "in Excel, to filter").
 *
 * PDF: the server draws the nine blocks with every name in them on one
 * landscape page and sends it back, the same way the session report already
 * works. It used to be a browser print window, which never opened: passing
 * `noopener` to window.open makes it return null, so the page reported a
 * blocked pop-up and wrote nothing.
 *
 * Excel does not draw the grid any more. It used to lay the nine blocks out
 * as nine columns of names, which lost the shape of the thing — you could not
 * see that block 1 is Exceptional by Turn — and could be neither sorted nor
 * filtered. The PDF is the picture; the workbook is what a spreadsheet is
 * actually good at (Victor, 8 Sep 2026): a "Blocks" sheet with one row per
 * block, and a "People" sheet with one row per person, both of which pivot.
 */
import { useState } from 'react'
import { FileDown, FileSpreadsheet } from 'lucide-react'
import toast from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import { talentReviewService, type TalentGridPerson } from '@/services/api/talentReviewService'
import {
  MIDYEAR_RATING_LABEL,
  POTENTIAL_LABEL,
  POTENTIAL_SHORT,
  RATING_PERIOD_SHORT,
  READINESS_LABEL,
  eduvosBlock,
  type Level,
  type Readiness,
} from './eduvosBlocks'

const BLOCK_ROWS: Level[] = ['high', 'medium', 'low']        // potential, top to bottom
const BLOCK_COLS: Level[] = ['low', 'medium', 'high']        // performance, left to right
const PERF_LABEL: Record<Level, string> = { low: 'Not yet full', medium: 'Full', high: 'Exceptional' }

const historyLine = (p: TalentGridPerson) =>
  (p.rating_history || []).map((r) => `${r.review_year} ${RATING_PERIOD_SHORT[r.review_type] || r.review_type}: ${MIDYEAR_RATING_LABEL[r.rating] || r.rating}`).join('; ')

function byBlock(people: TalentGridPerson[]) {
  const cells = new Map<number, TalentGridPerson[]>()
  for (const p of people) {
    if (p.block == null) continue
    cells.set(p.block, [...(cells.get(p.block) || []), p])
  }
  for (const list of cells.values()) list.sort((a, b) => a.name.localeCompare(b.name))
  return cells
}

export async function exportGridToExcel(people: TalentGridPerson[], title: string, reviewYear: number) {
  const XLSX = await import('xlsx')
  const cells = byBlock(people)
  const wb = XLSX.utils.book_new()

  // Sheet 1: one row per block. Sorts, charts and pivots; the shape of the
  // grid lives in the Impact and Potential columns rather than in the layout.
  const placedCount = people.filter((p) => p.block != null).length
  const blockRows = []
  for (const pot of BLOCK_ROWS) {
    for (const perf of BLOCK_COLS) {
      const block = eduvosBlock(perf, pot)
      if (!block) continue
      const list = cells.get(block.number) || []
      blockRows.push({
        Block: block.number,
        Label: block.label,
        Impact: PERF_LABEL[perf],
        Potential: POTENTIAL_SHORT[pot],
        People: list.length,
        'Share of placed (%)': placedCount ? Math.round((list.length / placedCount) * 100) : 0,
        'High-potential block': block.number <= 3 ? 'Yes' : '',
        Names: list.map((p) => p.name).join('; '),
      })
    }
  }
  blockRows.sort((a, b) => a.Block - b.Block)
  const blockSheet = XLSX.utils.json_to_sheet(blockRows)
  blockSheet['!cols'] = [
    { wch: 7 }, { wch: 30 }, { wch: 14 }, { wch: 10 }, { wch: 8 }, { wch: 18 }, { wch: 20 }, { wch: 60 },
  ]
  blockSheet['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: blockRows.length, c: 7 } }) }
  XLSX.utils.book_append_sheet(wb, blockSheet, 'Blocks')

  // The block a person sits in, by number, for the People sheet.
  const labelOfBlock = new Map(blockRows.map((b) => [b.Block, b.Label]))

  // Sheet 2: one row per person, what the card shows
  const rows = [...people]
    .sort((a, b) => (a.block ?? 99) - (b.block ?? 99) || a.name.localeCompare(b.name))
    .map((p) => ({
      Name: p.name,
      Title: p.job_title ?? '',
      Department: p.department ?? '',
      Function: p.function ?? '',
      Team: p.team ?? '',
      Campus: p.campus ?? '',
      'People Leader': p.leader_name ?? '',
      'One-Up': p.one_up_name ?? '',
      Block: p.block ?? '',
      'Block label': p.block != null ? labelOfBlock.get(p.block) ?? '' : '',
      Performance: p.performance ? PERF_LABEL[p.performance] : '',
      Potential: p.potential ? POTENTIAL_LABEL[p.potential] : '',
      'Mid-year rating': p.midyear_rating ? MIDYEAR_RATING_LABEL[p.midyear_rating] || p.midyear_rating : '',
      'Impact history': historyLine(p),
      'High-potential': p.is_hipo ? 'Yes' : '',
      'Key person': p.key_person ? 'Yes' : '',
      'Key person reason': p.key_person_reason ?? '',
      'Next role': p.next_role ?? '',
      Readiness: p.readiness ? READINESS_LABEL[p.readiness as Readiness] || p.readiness : '',
      'Tenure (years)': p.tenure_years ?? '',
      Contract: p.employment_category ?? '',
      'Growth plan gaps': (p.growth_plan_gaps || []).join('; '),
      Rationale: p.rationale ?? '',
      'Not placeable': p.not_placeable_reason ?? '',
    }))
  const peopleSheet = XLSX.utils.json_to_sheet(rows)
  const peopleCols = Object.keys(rows[0] || { Name: '' })
  peopleSheet['!cols'] = peopleCols.map((k) => ({ wch: k === 'Impact history' || k === 'Rationale' ? 48 : 20 }))
  // Filter buttons on every column: this is the sheet HR wanted to filter.
  peopleSheet['!autofilter'] = {
    ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: rows.length, c: Math.max(0, peopleCols.length - 1) } }),
  }
  peopleSheet['!freeze'] = { xSplit: 0, ySplit: 1 }
  XLSX.utils.book_append_sheet(wb, peopleSheet, 'People')

  XLSX.writeFile(wb, `${title.replace(/[^\w]+/g, '-').replace(/^-|-$/g, '') || 'talent-map'}-${reviewYear}.xlsx`)
}

export default function TalentGridExport({
  people,
  title,
  subtitle,
  reviewYear,
  pdfQuery,
  className,
}: {
  people: TalentGridPerson[]
  title: string
  subtitle: string
  reviewYear: number
  /** Scope and filters for the PDF: the server re-reads the same grid. */
  pdfQuery?: Omit<Parameters<typeof talentReviewService.downloadGridPdf>[0], 'reviewYear' | 'title'>
  className?: string
}) {
  const [building, setBuilding] = useState(false)
  const savePdf = async () => {
    setBuilding(true)
    try {
      await talentReviewService.downloadGridPdf({ reviewYear, title, ...(pdfQuery || {}) })
    } catch (e: any) {
      toast.error(e?.message || 'Could not build the PDF')
    } finally {
      setBuilding(false)
    }
  }
  return (
    <div className={className} data-demo="talent-grid-export">
      <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={savePdf} disabled={people.length === 0 || building} data-demo="talent-grid-pdf">
        <FileDown className="h-3.5 w-3.5" />
        {building ? 'Building…' : 'PDF'}
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="ml-2 h-8 gap-1.5 text-xs"
        disabled={people.length === 0}
        onClick={() => exportGridToExcel(people, title, reviewYear).catch((e) => toast.error(e?.message || 'Could not build the spreadsheet'))}
      >
        <FileSpreadsheet className="h-3.5 w-3.5" />
        Excel
      </Button>
    </div>
  )
}
